import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "@/App";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Redirect if already logged in
  if (user) {
    navigate("/app", { replace: true });
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email: form.email, password: form.password });
      login(res.data.token, res.data.user);
      toast.success("Welcome back");
      navigate("/app", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1771308136615-3cded1ed5cc0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwdHJ1Y2slMjBsb2dpc3RpY3MlMjB0cmFuc3BvcnR8ZW58MHx8fHwxNzczODc3Mzc1fDA&ixlib=rb-4.1.0&q=85"
          alt="Logistics"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <div className="flex items-center gap-4 mb-4">
            <img
              src="https://customer-assets.emergentagent.com/job_dispatch-pro-75/artifacts/edmtbm97_26023.jpg"
              alt="SGHK Logistics"
              className="w-16 h-16 rounded-sm object-contain bg-white/90 p-1"
            />
            <span className="font-heading text-2xl font-bold text-white tracking-tight">SGHK LOGISTICS</span>
          </div>
          <p className="text-zinc-300 text-lg max-w-md font-body">
            Complete transport & logistics management. Track jobs, manage fleet, generate invoices.
          </p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center bg-[#09090B] p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <img
              src="https://customer-assets.emergentagent.com/job_dispatch-pro-75/artifacts/edmtbm97_26023.jpg"
              alt="SGHK Logistics"
              className="w-10 h-10 rounded-sm object-contain bg-white/90 p-0.5"
            />
            <span className="font-heading text-xl font-bold text-white tracking-tight">SGHK LOGISTICS</span>
          </div>

          <h1 className="font-heading text-3xl font-bold text-white mb-1 tracking-tight">
            Team Sign In
          </h1>
          <p className="text-zinc-400 text-sm mb-8 font-body">
            Enter your credentials to continue
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 bg-zinc-900 border-zinc-800 text-white rounded-[2px] focus:ring-orange-500"
                placeholder="you@company.com"
                required
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Password</Label>
              <div className="relative mt-1.5">
                <Input
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="bg-zinc-900 border-zinc-800 text-white rounded-[2px] focus:ring-orange-500 pr-10"
                  placeholder="Min 6 characters"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-10 bg-orange-500 hover:bg-orange-600 text-white font-medium text-xs uppercase tracking-wider rounded-[2px] transition-colors active:scale-[0.98] disabled:opacity-50"
            >
              {loading ? "Please wait..." : "Sign In"}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-zinc-600">
            Authorised team members only. Contact admin for access.
          </p>
        </div>
      </div>
    </div>
  );
}
